import { BehaviorSubject, Subscription, Subject, Observable } from 'rxjs';
import { TreeNode } from 'primeng/api';
import { Injectable } from '@angular/core';
import { DataSelectDialogServiceInterface, IncludeOrExclude } from './data-select-dialog.service.interface';

/**
 * This abstract service defines the base implementation of the interface for the DataSelectDialogComponent.
 *
 * It defines the general logic for handling the selection and removal of items. It is used in Contact Editor,
 * and Targeting Search. Child classes are responsible for setting up initial state and converting data to TreeNodes
 * as necessary.
 */
@Injectable()
export abstract class AbstractDataSelectDialogService<T extends { id: string | number }> implements DataSelectDialogServiceInterface {
    public readonly dataTreeNodeType = 'default';
    protected availableItemTypes = new Map<string, string>();
    private readonly emptyMap = new Map(); // Used to reset selectedItemTypes so we don't have to create a new one every time there are no selections

    /** The entire list of TreeNodes after being converted */
    treeNodes: TreeNode[];
    protected treeNodesProvider: BehaviorSubject<TreeNode[]> = new BehaviorSubject(null);

    // TODO: delete? Unused
    protected dialiogVisibilityProvider: Subject<boolean> = new Subject();

    /** The tree (left side) items selected */
    selectedListItems: TreeNode[] = [];
    protected selectedListItemProvider: BehaviorSubject<TreeNode[]> = new BehaviorSubject(null);

    selectedItemTypes: Map<string, string> = new Map<string, string>();
    protected selectedItemTypesProvider: BehaviorSubject<Map<string, string>>;

    /** The display items (chips). Parents are kept collapsed when tree structure is used */
    selectedDisplayItems: TreeNode[];
    protected selectedDisplayItemsProvider: BehaviorSubject<TreeNode[]>;

    /** The initial state and final results of the service */
    selectedItems: T[] = [];

    subscriptions: Subscription[] = [];
    state: IncludeOrExclude;

    /** Flat relates to the structure of the data. Flat is 1D, non-flat is tree structure */
    isFlat: boolean;

    constructor(
        dataTypeLabel: string,
        dataTypeHeading: string,
        isFlat: boolean
    ) {
        this.isFlat = isFlat;
        this.selectedItemTypes.set(dataTypeLabel ?? this.dataTreeNodeType, dataTypeHeading ?? 'Data');
        this.selectedItemTypesProvider = new BehaviorSubject<Map<string, string>>(this.selectedItemTypes);
        this.selectedDisplayItemsProvider = new BehaviorSubject<TreeNode[]>(null);
    }

    /**
     * Converts typed data array to TreeNode array and sorts alphabetically.
     * Concrete implementations should be called in the constructor of the child class,
     * as this method operates on the data fetched by the back-end when this service is
     * initialized.
     *
     * @param dataItems the models to convert
     *
     * @returns TreeNode[] - the converted back-end data
     */
    protected abstract convertDataToTreeNodes(dataItems: T[]): TreeNode[];

    /**
     * Called by setState, mapping the given items (the initial state) to the
     * items set by convertDataToTreeNodes.
     *
     * @param dataItems the models to map
     *
     * @returns TreeNode[] - the initial state passed to it as TreeNodes
     */
    protected abstract mapSelectedDataToNodes(dataItems: T[]): TreeNode[];

    /**
     * Called by the handling class to intiialize the state of the service.
     *
     * @param state include or exclude
     * @param dataIds initial ids to use as starting state
     */
    abstract prepareData(state: IncludeOrExclude, dataIds: number[] | string[]);

    /**
     * Called by the handling class to intiialize the state of the service.
     *
     * @param state include or exclude
     * @param dataItems initial items used as starting state
     */
    abstract prepareModelData(state: IncludeOrExclude, dataItems: T[]);

    /**
     * Called by the DataSelectDialogComponent on apply. Sets the selectedItems so
     * the DataSelectDialogComponent can retrieve them and return them to parent class.
     * TODO: Remove selectedItems param, once Region and Language browsers are updated.
     * @param selectedItems Unused.
     */
    public applySelectedListItems(selectedItems: TreeNode[]): void {
        this.selectedItems = this.selectedListItems.map(item => <T>item.data);
    }

    /**
     * Initializes the service, setting preselected items (if any).
     */
    public setAppropriateListItems(): void {
        if (this.state === undefined) {
            return;
        }

        this.selectedListItems = this.mapSelectedDataToNodes(this.selectedItems);
        this.selectedListItemProvider.next(this.selectedListItems);
        this.updateDisplayItemsFromSelectedItems();
    }

    public itemSelected(item: TreeNode): void {}

    /**
     * Handles the logic between the display tree on the RHS and p-tree on the LHS
     * @param item a child item (if using tree-like structure)
     */
    public displayItemDeleted(item: TreeNode) {
        this.itemRemoved(item);
        this.selectedListItemProvider.next(this.selectedListItems);
    }

    public itemRemoved(item: TreeNode): void {
        const itemToDelete = (item.data as T).id;
        this.selectedListItems = this.selectedListItems.filter(listItem => listItem.data.id !== itemToDelete);
        this.updateDisplayItemsFromSelectedItems();
    }

    public nodeExpanded(item: TreeNode) {}

    public nodeCollapsed(item: TreeNode) {}

    /**
     * This is called before itemSelected or itemRemoved
     */
    public selectionChange(selectedItems: TreeNode[]): void {
        if (this.isFlat) {
            this.selectedListItems = selectedItems;
        } else {
            // We want to only "select" child nodes
            this.selectedListItems = selectedItems.filter(node => !!node.data.parent);
        }
        this.updateDisplayItemsFromSelectedItems();
    }

    /**
     * Getter for list item provider
     *
     * @returns BehaviorSubject<TreeNode[]>l
     */
    getListItems(): Observable<TreeNode[]> {
        return this.treeNodesProvider;
    }

    /**
     * Getter for SelectedListItems
     *
     * @returns BehaviorSubject<TreeNode[]>
     */
    getSelectedListItems(): Observable<TreeNode[]> {
        return this.selectedListItemProvider;
    }

    getSelectedItemTypes(): Observable<Map<string, string>> {
        return this.selectedItemTypesProvider;
    }

    getSelectedItemsToDisplay(): Observable<TreeNode[]> {
        return this.selectedDisplayItemsProvider;
    }

    getSelectedItems(): T[] {
        return this.selectedItems;
    }

    public filterItems(query: string): Observable<TreeNode[]> {
        return new Observable<TreeNode[]>(
            observer => {
                const filterString = query.toLocaleLowerCase();
                const filteredItems = [];
                if (this.isFlat) {
                    this.treeNodes.forEach(node => {
                        node.styleClass = 'hide';
                        if (node.label.toLocaleLowerCase().includes(filterString)) {
                            node.styleClass = '';
                            filteredItems.push(node);
                        }
                    });
                } else {
                    // TODO: Make recursive for trees with more than 2 levels
                    for (const parent of this.treeNodes) {
                        parent.styleClass = 'hide';
                        let childFound = false;
                        parent.children?.forEach(child => {
                            if (child.label.toLocaleLowerCase().includes(filterString)) {
                                child.styleClass = '';
                                childFound = true;
                            } else {
                                child.styleClass = 'hide';
                            }
                        });
                        if (childFound || parent.label.toLocaleLowerCase().includes(filterString)) {
                            parent.expanded = true;
                            parent.styleClass = '';
                            filteredItems.push(parent);
                        }
                    }
                }
                observer.next(filteredItems);
            }
        );
    }

    protected setData(state: IncludeOrExclude): void {
        this.state = state;
        this.setAppropriateListItems();
    }

    protected sortTreeItemsAlphabetically(a: TreeNode, b: TreeNode): number {
        const labelA = a.label.toLowerCase();
        const labelB = b.label.toLowerCase();

        return (labelA < labelB) ? -1 : (labelA > labelB) ? 1 : 0;
    }

    protected setSelectedItemTypes(): void {
        const selectedItemTypesMap = new Map<string, string>();
        this.selectedDisplayItems.forEach(item => {
            const itemType = item.data.type;
            const valueFromSelectedTypeMap = selectedItemTypesMap.get(itemType);
            if (valueFromSelectedTypeMap || itemType === null) {
                return;
            }

            selectedItemTypesMap.set(
                itemType,
                this.availableItemTypes.get(itemType)
            );
        });

        // assign item types in correct order
        this.selectedItemTypes = new Map(
            [...selectedItemTypesMap.entries()].sort(([, a], [, b]) => a.localeCompare(b))
        );
    }

    /**
     * Determines any children that are reduntantly selected (because their parent is selected),
     * and removes them.
     * @returns a filtered array containing only display-ready (collapsed) items
     */
    private collapseChildrenToParents(): TreeNode<any>[] {
        let selectedItems = this.selectedListItems.slice();

        const parents = selectedItems.filter(x => !!x.children);
        // If all children are selected, we can simplify it as simply the parent being selected
        parents.forEach(parent => {
            const selectedChildren = selectedItems.filter(x => x.parent && x.parent.data.id === parent.data.id);

            if (parent.children.length === selectedChildren.length) {
                selectedItems = selectedItems.filter(x => !x.parent || x.parent.data.id !== parent.data.id);
            }
        });

        return selectedItems.sort(this.sortTreeItemsAlphabetically);
    }

    protected convertListItemsToDisplayItems(): TreeNode<any>[] {
        // if (this.isFlat) {
        return this.selectedListItems.slice();
        // }

        // // TODO: For Not flat and not collapse => Filter out parents
        // return this.collapseChildrenToParents();
    }

    protected updateDisplayItemsFromSelectedItems() {
        this.selectedDisplayItems = this.convertListItemsToDisplayItems();
        this.selectedDisplayItemsProvider.next(this.selectedDisplayItems);
        if (!this.isFlat) {
            this.setSelectedItemTypes();
            this.updateSelectedTypes();
            this.selectedDisplayItemsProvider.next(this.selectedDisplayItems);
        }
    }

    protected updateSelectedTypes(): void {
        if (!this.selectedDisplayItems || !this.selectedDisplayItems.length) {
            this.selectedItemTypesProvider.next(this.emptyMap);
            return;
        }

        this.selectedItemTypesProvider.next(this.selectedItemTypes);
    }

    reset(): void {
        this.selectedListItems = [];
        this.selectedListItemProvider.next(this.selectedListItems);
        this.selectedDisplayItems = [];
        this.selectedDisplayItemsProvider.next(this.selectedDisplayItems);
    }
}
